import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import * as Clipboard from 'expo-clipboard';

import { Palette, radius, spacing, useThemePalette } from '../theme';
import { track } from '../lib/analytics';
import { ApplyField } from '../lib/apply/applyFields';

interface ApplyKitChipsProps {
  fields: ApplyField[];
  // Show the "Apply kit" title + hint above the chips. Off when the panel is
  // already labelled by its container (e.g. the in-app browser toggle).
  showHeader?: boolean;
}

// One-tap copy of the primary resume's contact fields so they're ready to paste
// into an external application form. Shared by the job Description modal and the
// in-app ApplyBrowser. Renders nothing when there's nothing to copy.
const ApplyKitChips: React.FC<ApplyKitChipsProps> = ({ fields, showHeader = true }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the "copied" reset timer on unmount so it never fires setState on an
  // unmounted view (and isn't left dangling on fast repeat taps).
  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    []
  );

  if (fields.length === 0) return null;

  const copyField = async (field: ApplyField) => {
    await Clipboard.setStringAsync(field.value);
    setCopiedKey(field.key);
    track('apply_kit_copy_field', { field: field.key });
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopiedKey(null), 1500);
  };

  return (
    <View style={styles.applyKit}>
      {showHeader && (
        <>
          <Text style={styles.applyKitTitle}>{t('applyKit.title')}</Text>
          <Text style={styles.applyKitHint}>{t('applyKit.hint')}</Text>
        </>
      )}
      <View style={styles.applyKitChips}>
        {fields.map((field) => {
          const copied = copiedKey === field.key;
          return (
            <TouchableOpacity
              key={field.key}
              style={styles.applyChip}
              onPress={() => copyField(field)}
              activeOpacity={0.7}
              accessibilityLabel={`${t('applyKit.title')}: ${t(`resume.fields.${field.key}`)}`}
            >
              <MaterialCommunityIcons
                name={copied ? 'check' : 'content-copy'}
                size={13}
                color={copied ? palette.appliedText : palette.textSecondary}
              />
              <Text style={styles.applyChipText}>
                {copied ? t('applyKit.copied') : t(`resume.fields.${field.key}`)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    applyKit: {
      marginTop: spacing.sm,
      paddingTop: spacing.sm,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.border,
    },
    applyKitTitle: { fontSize: 13, fontWeight: '700', color: p.textPrimary },
    applyKitHint: { fontSize: 12, color: p.textSecondary, marginTop: 2, marginBottom: spacing.sm },
    applyKitChips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
    applyChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: p.surfaceMuted,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
      borderRadius: radius.pill,
    },
    applyChipText: { fontSize: 12, color: p.textSecondary, fontWeight: '600' },
  });

export default ApplyKitChips;
